import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  Heading,
  Icon,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { DocumentTextIcon } from '@heroicons/react/outline';
import { EyeIcon } from '@heroicons/react/solid';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { FileRejection } from 'react-dropzone';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { FormErrors, FundraisingDocumentTemplatePath, noop } from '@blockpulse3/data/shared';
import {
  DocumentSignatureStepType,
  FundraisingDocumentType,
  useDeleteSubscriptionTemplateMutation,
  useGetFundraisingQuery,
  usePreviewMockSubscriptionTemplateLazyQuery,
  useValidateSubscriptionTemplateMutation,
} from '@blockpulse3/graphql/hooks';
import { b64toBlob } from '@blockpulse3/helpers';
import {
  CONTENT_LOADING,
  DropzoneInput,
  ErrorMessage,
  PreviewDocumentModal,
} from '@blockpulse3/ui/commons';

import { FundraisingDocumentationType, INewFundraisingDocumentationForm } from './types';

type Props = {
  /* ** Callback on submit error ** */
  onSubmitError?: () => void;
  /* ** Callback on submit completed ** */
  onSubmitCompleted?: () => void;
  /* ** Callback on file upload cancel ** */
  onCancel: () => void;
};

/**
 * NewFundraisingDocumentation.
 * Documentation fundraising step form used in both Private and Crowdfunding funnels.
 *
 * @param {Props}
 * @returns {JSX.Element}
 */
export function NewFundraisingDocumentation({
  onSubmitCompleted = noop,
  onSubmitError = noop,
  onCancel = noop,
}: Props): JSX.Element {
  const i18nFundraisingDocumentationForm = useTranslations('FundraisingDocumentationForm');

  const [isFileLoading, setIsFileLoading] = useState<boolean>(false);
  const [templateUrl, setTemplateUrl] = useState<string | symbol | null>(CONTENT_LOADING);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const { fundraisingId = '', companyId = '' } = useParams();

  const [validateSubscriptionTemplate] = useValidateSubscriptionTemplateMutation();

  const { data, refetch, loading } = useGetFundraisingQuery({
    variables: {
      fundraisingId,
    },
    skip: !fundraisingId,
  });

  const fundraising = data?.fundraising;

  /* ** Find the corresponding step ** */
  const documentSignatureStep = fundraising?.subscriptionSteps?.find(
    (step) =>
      step.__typename === 'DocumentSignatureStepType' &&
      step.documentType === FundraisingDocumentType.SUBSCRIPTION,
  ) as DocumentSignatureStepType;

  const [deleteTemplate] = useDeleteSubscriptionTemplateMutation();
  const [previewTemplate] = usePreviewMockSubscriptionTemplateLazyQuery();

  const { control, handleSubmit, formState, setError, setValue, clearErrors } =
    useForm<INewFundraisingDocumentationForm>({
      defaultValues: { documentationType: FundraisingDocumentationType.DEFAULT },
    });

  const fileName = documentSignatureStep?.templateName
    ? {
        [documentSignatureStep.id]: new File(
          [documentSignatureStep.templateName],
          documentSignatureStep.templateName,
        ),
      }
    : {};

  /* ** Method to upload a file using axios ** */
  const handleFileUpload = async (acceptedFiles: File[]): Promise<void> => {
    if (!fundraisingId) return;

    const formData = new FormData();
    formData.append('subscriptionTemplate', acceptedFiles[0]);
    setIsFileLoading(true);
    await axios
      .post(
        process.env['NX_API_CONTROLLER_ENDPOINT'] +
          '/fundraisings/' +
          fundraisingId +
          '/importSubscriptionTemplate',
        formData,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data',
          },
        },
      )
      .catch(() => {
        setError('templateFile', { type: 'custom', message: FormErrors.DropzoneInvalidTemplate });
      });
    await refetch();
    setIsFileLoading(false);
  };

  /* ** Upload file handler ** */
  const handleFileDrop = (acceptedFiles: File[], fileRejections: FileRejection[]): void => {
    clearErrors('templateFile');
    if (fileRejections.length === 0) {
      handleFileUpload(acceptedFiles);
    } else {
      const error = fileRejections[0].errors[0].code;
      switch (error) {
        case 'file-too-large': {
          setError('templateFile', { type: 'custom', message: FormErrors.FileTooLarge });
          break;
        }
        default: {
          setError('templateFile', { type: 'custom', message: FormErrors.DropzoneDefault });
          break;
        }
      }
    }
  };

  /* ** Delete file handler ** */
  const handleFileDelete = (): void => {
    if (fundraisingId) {
      deleteTemplate({
        variables: {
          deleteSubscriptionTemplateInput: {
            fundraisingId,
          },
        },
        onCompleted: () => {
          setValue('templateFile', undefined);
          refetch();
        },
        onError: () => {
          setError('templateFile', { type: 'custom', message: FormErrors.DropzoneDefault });
        },
      });
    }
  };

  const handleFormSubmit: SubmitHandler<INewFundraisingDocumentationForm> = (data): void => {
    if (data.documentationType === FundraisingDocumentationType.TEMPLATE && !data.templateFile) {
      setError('templateFile', { type: 'custom', message: FormErrors.FileRequired });
    } else {
      const isTemplateEnabled = data.documentationType === FundraisingDocumentationType.TEMPLATE;
      validateSubscriptionTemplate({
        variables: {
          validateSubscriptionTemplateInput: {
            fundraisingId,
            isTemplateEnabled,
          },
        },
        onCompleted: () => {
          refetch();
          onSubmitCompleted();
        },
        onError: () => {
          onSubmitError();
        },
      });
    }
  };

  const handleFormCancel = (): void => {
    if (!companyId) {
      return;
    }
    onCancel();
  };

  const handleFilePreview = (templateUrl?: string): void => {
    if (!fundraising) return;
    setTemplateUrl(CONTENT_LOADING);
    onOpen();

    previewTemplate({
      variables: {
        previewSubscriptionTemplateInput: {
          fundraisingId: fundraising.id,
          templateUrl,
        },
      },
      fetchPolicy: 'no-cache',
      onCompleted({ previewMockSubscriptionTemplate: pdfURL }) {
        let objURL: string;
        if (pdfURL.startsWith('http')) {
          objURL = pdfURL;
        } else {
          const blob = b64toBlob(pdfURL, 'application/pdf');
          objURL = URL.createObjectURL(blob);
        }
        setTemplateUrl(objURL);
      },
    });
  };

  /* ** Set value of template file if exist ** */
  useEffect(() => {
    if (documentSignatureStep?.templateName) {
      setValue('templateFile', documentSignatureStep.id);
    }
    if (documentSignatureStep?.isTemplateEnabled) {
      setValue('documentationType', FundraisingDocumentationType.TEMPLATE);
    }
  }, [documentSignatureStep, setValue]);

  return (
    <>
      <Card variant="divider-top">
        <CardHeader py="6">
          <Stack spacing="4">
            <Heading size="lg">{i18nFundraisingDocumentationForm('FormTitle')}</Heading>
            <Text>{i18nFundraisingDocumentationForm('FormSubtitle')}</Text>
          </Stack>
        </CardHeader>
        <Divider />
        <CardBody py="6">
          <form id="subscription-document-type" onSubmit={handleSubmit(handleFormSubmit)}>
            <Controller
              control={control}
              name="documentationType"
              render={({ field }): JSX.Element => (
                <FormControl isInvalid={!!formState.errors.documentationType} w="full">
                  <FormLabel fontSize="xl" pb="2">
                    {i18nFundraisingDocumentationForm('SubscriptionDocument')}
                  </FormLabel>
                  <RadioGroup as={Stack} spacing="4" {...field}>
                    <Radio
                      alignItems="center"
                      as={HStack}
                      value={FundraisingDocumentationType.DEFAULT}
                      variant="solid"
                    >
                      <Text as="label">
                        {i18nFundraisingDocumentationForm('DefaultDocument')}
                        <IconButton
                          aria-label="preview"
                          icon={<Icon as={EyeIcon} boxSize="20px" color="gray.500" />}
                          variant="unstyled"
                          onClick={(): void =>
                            handleFilePreview(FundraisingDocumentTemplatePath.SUBSCRIPTION)
                          }
                        />
                      </Text>
                    </Radio>
                    <Radio value={FundraisingDocumentationType.TEMPLATE} variant="solid">
                      {i18nFundraisingDocumentationForm('TemplateDocument')}
                      <Stack pt="4">
                        <Controller
                          control={control}
                          name="templateFile"
                          render={(): JSX.Element => (
                            <FormControl>
                              <DropzoneInput
                                isPreviewEnabled
                                files={fileName}
                                isLoading={isFileLoading}
                                maxFiles={1}
                                subTitle=".DOCX"
                                accept={{
                                  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                                    [],
                                }}
                                icon={
                                  <Icon
                                    as={DocumentTextIcon}
                                    boxSize="42px"
                                    color="gray.400"
                                    strokeWidth="1px"
                                  />
                                }
                                onDelete={handleFileDelete}
                                onDrop={handleFileDrop}
                                onPreview={(): void => handleFilePreview()}
                              />
                              <ErrorMessage error={formState.errors?.templateFile} />
                            </FormControl>
                          )}
                        />
                      </Stack>
                    </Radio>
                  </RadioGroup>
                </FormControl>
              )}
            />
          </form>
        </CardBody>
        <CardFooter as={HStack} spacing="4">
          <Button type="button" variant="secondary" w="full" onClick={handleFormCancel}>
            {i18nFundraisingDocumentationForm('Back')}
          </Button>
          <Button
            data-cy="next"
            form="subscription-document-type"
            isDisabled={isFileLoading || loading}
            type="submit"
            w="full"
          >
            {i18nFundraisingDocumentationForm('Next')}
          </Button>
        </CardFooter>
      </Card>
      <PreviewDocumentModal
        isOpen={isOpen}
        src={templateUrl}
        title={i18nFundraisingDocumentationForm('PreviewTitle')}
        onClose={onClose}
      />
    </>
  );
}

export type NewFundraisingDocumentationProps = Props;
